import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { exportOrder } from "@/lib/exportOrder";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Users, Clock, Activity, TrendingUp, TrendingDown, Download, Loader2, LogIn, FileSignature } from "lucide-react";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const RED = "#DC2626";
const AMBER = "#D97706";
const PIE = [NAVY, GREEN, AMBER, RED, "#0EA5E9", "#7C3AED"];

const RANGES = [
  ["1", "Bugün"], ["2", "Dün ve bugün"], ["3", "3 gün"], ["5", "5 gün"],
  ["7", "7 gün"], ["15", "15 gün"], ["30", "30 gün"], ["all", "Tüm zamanlar"],
  ["custom", "Özel tarih aralığı"],
];

const fmtMin = (m) => (!m ? "0 dk" : m >= 60 ? `${Math.floor(m / 60)} sa ${m % 60} dk` : `${m} dk`);
const fmtDt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

const Kpi = ({ label, value, sub, icon: Icon, color }) => (
  <Card className="p-4" data-testid={`ur-kpi-${label}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <Icon className="h-4 w-4 opacity-40" />
    </div>
  </Card>
);

export default function UserReports() {
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("ozet");
  const [monthly, setMonthly] = useState(null);
  const [pool, setPool] = useState(null);
  const [period, setPeriod] = useState("month");

  const params = useMemo(() => {
    if (range === "custom") return { ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) };
    if (range === "all") return {};
    return { days: Number(range) };
  }, [range, from, to]);

  useEffect(() => {
    if (range === "custom" && !from && !to) return;
    setLoading(true);
    api.get("/reports/user-performance", { params })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Rapor yüklenemedi"))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    if (!sel) { setDetail(null); return; }
    api.get("/reports/user-performance/detail", { params: { name: sel, ...params } })
      .then(({ data }) => setDetail(data))
      .catch(() => toast.error("Detay yüklenemedi"));
  }, [sel, params]);

  useEffect(() => {
    if (tab !== "aylik") return;
    setMonthly(null);
    api.get("/reports/user-performance/monthly", { params: { period, count: 12 } })
      .then(({ data }) => setMonthly(data))
      .catch(() => toast.error("Dönem karşılaştırma yüklenemedi"));
  }, [tab, period]);

  const t = useTableSort(data?.rows || []);
  const plT = useTableSort(pool?.rows || []);
  const chT = useTableSort([]);
  const cmT = useTableSort([]);
  const seT = useTableSort([]);

  const doExport = async () => {
    setExporting(true);
    try {
      const order = t.sorted.map((r) => r.name).join(",");
      const res = await api.get("/reports/user-performance/export", { params: { ...params, order, only: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "kullanici_performans.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel indirilemedi");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (tab !== "havuz") return;
    setPool(null);
    api.get("/reports/pool-leaderboard", { params })
      .then(({ data }) => setPool(data))
      .catch(() => toast.error("Havuz liderliği yüklenemedi"));
  }, [tab, params]);

  const doPoolExport = async () => {
    try {
      const order = await exportOrder(plT.sorted.map((r) => r.name));
      const res = await api.get("/reports/pool-leaderboard/export", { params: { ...params, order, only: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "havuz_liderligi.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel indirilemedi");
    }
  };

  const doCardPdf = async (name) => {
    try {
      const res = await api.get("/reports/user-performance/card/pdf",
        { params: { ...params, ...(name ? { name } : {}) }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name ? `karne_${name.replace(/\s+/g, "_")}.pdf` : "kullanici_karneleri.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Karne PDF indirilemedi");
    }
  };

  const tot = data?.totals || {};
  const netChart = (data?.rows || []).filter((r) => (r.portfolio?.firms || 0) > 0).slice(0, 12)
    .map((r) => ({ name: r.name.split(" ")[0], kesin: r.portfolio?.kesin || 0, karsi: r.portfolio?.karsi || 0 }));

  return (
    <div className="space-y-4" data-testid="user-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Kullanıcı Karnesi</h1>
          <p className="text-sm text-muted-foreground">
            Oylar ve işlemler firmanın <strong>sorumlusuna</strong> yazılır (sorumlu yoksa işlemi girene). Tarih aralığı seçtiğinizde portföy sütunları da <strong>yalnız o dönemde işlem yapılan firmaları</strong> gösterir; tam portföy için "Tüm zamanlar" seçin.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="ur-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {range === "custom" && (
            <>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" data-testid="ur-from" />
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" data-testid="ur-to" />
            </>
          )}
          <Button variant="outline" size="sm" className="h-9" onClick={doExport} disabled={exporting} data-testid="ur-export">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Excel
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => doCardPdf(null)} data-testid="ur-cards-pdf">
            <Download className="h-4 w-4 mr-1.5" />Tüm Karneler PDF
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <Card className="p-10 text-center text-muted-foreground">Yükleniyor...</Card>
      ) : !data ? null : (
        <>
          <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid="ur-tabs">
            {[["ozet", "Karne"], ["aylik", "Dönem Karşılaştırma"], ["havuz", "Havuz Liderliği"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`ur-tab-${k}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          {tab === "havuz" ? (
            !pool ? (
              <Card className="p-10 text-center text-muted-foreground">Yükleniyor...</Card>
            ) : (
              <div className="space-y-4" data-testid="ur-pool-leaderboard">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Kpi label="Devralınan firma" value={pool.totals.claimed} icon={TrendingUp} color={GREEN} sub="havuzdan sahiplenildi" />
                  <Kpi label="İlgilenilen firma" value={pool.totals.interest} icon={Activity} color={NAVY} sub="ilgileniyorum denildi" />
                  <Kpi label="Havuza eklenen" value={pool.totals.added} icon={FileSignature} color={AMBER} sub="sahaya açıldı" />
                  <Kpi label="Katkı veren kişi" value={pool.totals.people} icon={Users} color={NAVY} sub={`${pool.range.from} → ${pool.range.to}`} />
                </div>

                {pool.rows.length >= 2 && (
                  <Card className="p-4">
                    <p className="text-sm font-medium mb-3">En çok havuz hareketi (ilk 10)</p>
                    <div style={{ width: "100%", height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                        <BarChart data={pool.rows.slice(0, 10).map((r) => ({ name: r.name.split(" ")[0], Devraldığı: r.pool_claimed, İlgilendiği: r.pool_interest }))}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" fontSize={11} />
                          <YAxis allowDecimals={false} fontSize={10} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="Devraldığı" stackId="a" fill={GREEN} radius={[0, 0, 0, 0]} />
                          <Bar dataKey="İlgilendiği" stackId="a" fill={NAVY} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}

                <Card className="p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-3">
                    <div>
                      <p className="text-sm font-medium">Havuz Liderliği</p>
                      <p className="text-xs text-muted-foreground">Sıralama: devraldığı + ilgilendiği toplamı. Yalnız havuz hareketi olanlar listelenir.</p>
                    </div>
                    <Button variant="outline" size="sm" className="h-9 ml-auto" onClick={doPoolExport} data-testid="ur-pool-export">
                      <Download className="h-4 w-4 mr-1.5" />Excel
                    </Button>
                  </div>
                  {pool.rows.length === 0 ? (
                    <p className="p-10 text-center text-sm text-muted-foreground" data-testid="ur-pool-empty">
                      Seçilen tarih aralığında havuz hareketi yok.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="ur-pool-table">
                        <thead>
                          <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                            <th className="px-3 py-3 font-medium">Sıra</th>
                            <plT.Th sortKey="pl-ad" get={(r) => r.name} text className="px-3 py-3 font-medium">Kullanıcı</plT.Th>
                            <plT.Th sortKey="pl-rol" get={(r) => r.role_label} text className="px-3 py-3 font-medium">Rol</plT.Th>
                            <plT.Th sortKey="pl-devir" get={(r) => r.pool_claimed} className="px-3 py-3 font-medium text-right">Devraldığı</plT.Th>
                            <plT.Th sortKey="pl-ilgi" get={(r) => r.pool_interest} className="px-3 py-3 font-medium text-right">İlgilendiği</plT.Th>
                            <plT.Th sortKey="pl-toplam" get={(r) => r.total} className="px-3 py-3 font-medium text-right">Toplam</plT.Th>
                            <plT.Th sortKey="pl-ekledi" get={(r) => r.pool_added} className="px-3 py-3 font-medium text-right" title="Havuza kendi eklediği firma sayısı">Havuza Eklediği</plT.Th>
                          </tr>
                        </thead>
                        <tbody>
                          {plT.sorted.map((r, i) => (
                            <tr key={r.name} className="border-t border-border/60 hover:bg-muted/40" data-testid={`ur-pool-row-${r.name}`}>
                              <td className="px-3 py-2 tabular-nums">
                                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                              </td>
                              <td className="px-3 py-2 font-medium">{r.name}</td>
                              <td className="px-3 py-2 text-muted-foreground">{r.role_label}</td>
                              <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{r.pool_claimed}</td>
                              <td className="px-3 py-2 text-right tabular-nums" style={{ color: NAVY }}>{r.pool_interest}</td>
                              <td className="px-3 py-2 text-right tabular-nums font-bold">{r.total}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.pool_added}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            )
          ) : tab === "aylik" ? (
            <Card className="p-0 overflow-hidden" data-testid="ur-monthly-card">
              <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-3">
                <div>
                  <p className="text-sm font-medium">Dönem karşılaştırma — kim hızlandı, kim yavaşladı</p>
                  <p className="text-xs text-muted-foreground">Her hücre: o dönemdeki kesin kazanımı · işlem sayısı. Ok, önceki döneme göre gidişi gösterir. Son 12 dönem.</p>
                </div>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="h-9 w-[170px] ml-auto" data-testid="ur-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[["day", "Günlük"], ["3day", "3 Günlük"], ["5day", "5 Günlük"], ["week", "Haftalık"],
                      ["15day", "15 Günlük"], ["month", "Aylık"], ["all", "Tüm Zamanlar"]].map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!monthly ? <div className="p-8 text-center text-muted-foreground text-sm">Yükleniyor...</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ur-monthly-table">
                    <thead>
                      <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                        <th className="px-3 py-3 font-medium sticky left-0 bg-muted/60">Kullanıcı</th>
                        {monthly.months.map((m, i) => <th key={m} className="px-3 py-3 font-medium text-center whitespace-nowrap">{monthly.labels?.[i] || m}</th>)}
                        <th className="px-3 py-3 font-medium text-right">Toplam Kesin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly.rows.map((r) => (
                        <tr key={r.name} className="border-t border-border/60 hover:bg-muted/40" data-testid={`ur-monthly-row-${r.name}`}>
                          <td className="px-3 py-2 font-medium whitespace-nowrap sticky left-0 bg-card">{r.name}</td>
                          {r.cells.map((c) => (
                            <td key={c.month} className="px-3 py-2 text-center tabular-nums">
                              <span className="font-semibold" style={{ color: c.kesin ? GREEN : undefined }}>{c.kesin}</span>
                              <span className="text-muted-foreground text-xs"> · {c.changes}</span>
                              {c.trend !== 0 && (
                                <span className="ml-1 text-xs" style={{ color: c.trend > 0 ? GREEN : RED }}>{c.trend > 0 ? "▲" : "▼"}</span>
                              )}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-right tabular-nums font-bold">{r.total_kesin}</td>
                        </tr>
                      ))}
                      {!monthly.rows.length && (
                        <tr><td colSpan={monthly.months.length + 2} className="px-3 py-10 text-center text-muted-foreground">Kayıt yok.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ) : (
          <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            <Kpi label="Portföy kesin oy" value={tot.portfolio_kesin ?? 0} icon={TrendingUp} color={GREEN}
              sub={`${tot.unassigned_kesin ?? 0} tanesi sorumsuz`} />
            <Kpi label="Portföy karşı" value={tot.portfolio_karsi ?? 0} icon={TrendingDown} color={RED}
              sub={`${tot.portfolio_firms ?? 0} FAAL firma`} />
            <Kpi label="Yetki belgesi" value={tot.portfolio_belge ?? 0} icon={FileSignature} color={GREEN}
              sub={`dönemde +${tot.belge ?? 0}`} />
            <Kpi label="Şu an aktif" value={tot.active_now ?? 0} icon={Activity} color={GREEN} sub={`${tot.users ?? 0} kullanıcı çalıştı`} />
            <Kpi label="Oturum" value={tot.sessions ?? 0} icon={LogIn} color={NAVY} sub={`ort. ${tot.avg_session ?? 0} dk`} />
            <Kpi label="Toplam süre" value={fmtMin(tot.work_minutes ?? tot.minutes)} icon={Clock} color={NAVY} sub={`oturumlarda ${fmtMin(tot.minutes)}`} />
            <Kpi label="İşlem" value={tot.changes ?? 0} icon={Users} color={NAVY} sub={`${tot.gained ?? 0} kesin kazanıldı · ${tot.calls ?? 0} arama/not`} />
            <Kpi label="Havuz" value={tot.pool_claimed ?? 0} icon={Users} color={NAVY} sub={`devralındı · ${tot.pool_interest ?? 0} firma ile ilgilenildi`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-4 lg:col-span-2">
              <p className="text-sm font-medium mb-3">Kişi bazlı portföy (kesin / karşı oy)</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={netChart} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="kesin" name="Kesin oy" fill={GREEN} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="karsi" name="Karşı" fill={RED} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-4">
              <p className="text-sm font-medium mb-3">Rol dağılımı (çalışan kullanıcı)</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.roles} dataKey="value" nameKey="name" outerRadius={80} label>
                      {data.roles.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <p className="text-sm font-medium mb-3">Günlük aktivite</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="minutes" name="Ekran dakikası" stroke={NAVY} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="changes" name="Değişiklik" stroke={AMBER} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="gained" name="Kazanılan oy" stroke={GREEN} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-4">
              <p className="text-sm font-medium mb-3">Saatlere göre yoğunluk</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.hourly} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="minutes" name="Dakika" fill={NAVY} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <p className="text-sm font-medium">Kullanıcı Karnesi</p>
              <span className="text-xs text-muted-foreground">{data.range.from} → {data.range.to} · satıra tıklayın</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ur-table">
                <thead>
                  <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                    <t.Th sortKey="u-ad" get={(r) => r.name} text className="px-3 py-3 font-medium">Kullanıcı</t.Th>
                    <t.Th sortKey="u-rol" get={(r) => r.role_label} text className="px-3 py-3 font-medium">Rol</t.Th>
                    <t.Th sortKey="u-pf-firma" get={(r) => r.portfolio?.firms || 0} className="px-3 py-3 font-medium text-right" title="Sorumlusu olduğu FAAL firma sayısı">Firma</t.Th>
                    <t.Th sortKey="u-pf-kesin" get={(r) => r.portfolio?.kesin || 0} className="px-3 py-3 font-medium text-right" title="Sorumlusu olduğu FAAL firmalarda kesin oy">Kesin</t.Th>
                    <t.Th sortKey="u-pf-belki" get={(r) => r.portfolio?.belki || 0} className="px-3 py-3 font-medium text-right">Belki</t.Th>
                    <t.Th sortKey="u-pf-ulas" get={(r) => r.portfolio?.ulasilamadi || 0} lowBest className="px-3 py-3 font-medium text-right">Ulaşılamadı</t.Th>
                    <t.Th sortKey="u-pf-karsi" get={(r) => r.portfolio?.karsi || 0} lowBest className="px-3 py-3 font-medium text-right">Karşı</t.Th>
                    <t.Th sortKey="u-pf-bos" get={(r) => r.portfolio?.bos || 0} lowBest className="px-3 py-3 font-medium text-right">İşaretsiz</t.Th>
                    <t.Th sortKey="u-pf-belge" get={(r) => r.portfolio?.belge || 0} className="px-3 py-3 font-medium text-right">Belge</t.Th>
                    <t.Th sortKey="u-oturum" get={(r) => r.sessions} className="px-3 py-3 font-medium text-right">Giriş</t.Th>
                    <t.Th sortKey="u-sure" get={(r) => r.work_minutes ?? r.minutes} className="px-3 py-3 font-medium text-right" title="Oturum süreleri ve işlem yoğunluğundan hesaplanan çalışma süresi">Süre</t.Th>
                    <t.Th sortKey="u-gun" get={(r) => r.days_worked} className="px-3 py-3 font-medium text-right" title="Kaç ayrı günde çalıştı">Gün</t.Th>
                    <t.Th sortKey="u-seri" get={(r) => r.streak_best} className="px-3 py-3 font-medium text-right" title="Üst üste çalıştığı en uzun gün serisi">Seri</t.Th>
                    <t.Th sortKey="u-giris" get={(r) => r.last_login} text className="px-3 py-3 font-medium">Son giriş</t.Th>
                    <t.Th sortKey="u-degisiklik" get={(r) => r.changes} className="px-3 py-3 font-medium text-right" title="Sorumlusu olduğu firmalarda yapılan işlem sayısı">İşlem</t.Th>
                    <t.Th sortKey="u-kazanc" get={(r) => r.gained} className="px-3 py-3 font-medium text-right" title="Dönem içinde kesine dönen oy">Kesin +</t.Th>
                    <t.Th sortKey="u-kayip" get={(r) => r.lost} lowBest className="px-3 py-3 font-medium text-right" title="Dönem içinde kesinden çıkan oy">Karşı +</t.Th>
                    <t.Th sortKey="u-arama" get={(r) => r.calls} className="px-3 py-3 font-medium text-right">Arama/Not</t.Th>
                    <t.Th sortKey="u-havuz-devir" get={(r) => r.pool_claimed || 0} className="px-3 py-3 font-medium text-right" title="Havuzdan kendi sorumluluğuna aldığı firma sayısı">Devraldığı</t.Th>
                    <t.Th sortKey="u-havuz-ilgi" get={(r) => r.pool_interest || 0} className="px-3 py-3 font-medium text-right" title="Havuzda 'ilgileniyorum' dediği firma sayısı">İlgilendiği</t.Th>
                    <t.Th sortKey="u-komite" get={(r) => r.committee_count} className="px-3 py-3 font-medium text-right">Komite</t.Th>
                    <th className="px-3 py-3 font-medium text-right">Karne</th>
                  </tr>
                </thead>
                <tbody>
                  {t.sorted.map((r) => (
                    <tr key={r.name} onClick={() => setSel(r.name)}
                      className="border-t border-border/60 hover:bg-muted/40 cursor-pointer"
                      data-testid={`ur-row-${r.name}`}>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.role_label}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.portfolio?.firms || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: GREEN }}>{r.portfolio?.kesin || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums" style={{ color: AMBER }}>{r.portfolio?.belki || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.portfolio?.ulasilamadi || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums" style={{ color: RED }}>{r.portfolio?.karsi || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.portfolio?.bos || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.portfolio?.belge || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.sessions}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMin(r.work_minutes ?? r.minutes)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.days_worked}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.streak_best}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtDt(r.last_login)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.changes}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{r.gained}</td>
                      <td className="px-3 py-2 text-right tabular-nums" style={{ color: RED }}>{r.lost}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.calls}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: NAVY }}>{r.pool_claimed || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.pool_interest || 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.committee_count}</td>
                      <td className="px-3 py-2 text-right">
                        {r.name !== "— Sorumsuz" && (
                          <button onClick={(e) => { e.stopPropagation(); doCardPdf(r.name); }}
                            className="text-xs font-medium text-[#0F2C59] dark:text-sky-300 hover:underline"
                            data-testid={`ur-pdf-${r.name}`}>PDF</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!t.sorted.length && (
                    <tr><td colSpan={20} className="px-3 py-10 text-center text-muted-foreground">Bu aralıkta kayıt yok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
          </>
          )}
        </>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ur-detail-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-3">
            <span>{sel} · karne detayı</span>
            <Button size="sm" variant="outline" onClick={() => doCardPdf(sel)} data-testid="ur-detail-pdf">
              <Download className="h-4 w-4 mr-1.5" />Karne PDF
            </Button>
          </DialogTitle></DialogHeader>
          {!detail ? <p className="text-sm text-muted-foreground">Yükleniyor...</p> : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-center">
                {[
                  ["Portföy firma", detail.portfolio?.firms ?? 0], ["Kesin oy", detail.portfolio?.kesin ?? 0],
                  ["Karşı", detail.portfolio?.karsi ?? 0], ["Yetki belgesi", detail.portfolio?.belge ?? 0],
                  ["Giriş", detail.totals.sessions], ["Toplam süre", fmtMin(detail.totals.work_minutes ?? detail.totals.minutes)],
                  ["Çalışılan gün", detail.totals.days_worked], ["Üst üste gün", detail.totals.streak_best],
                  ["Ekranda", fmtMin(detail.totals.active_minutes)], ["İşlem (sorumlu)", detail.totals.changes],
                  ["Kendi girdiği", detail.totals.own_entries], ["Dönemde kesin +", detail.totals.gained],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-border p-2">
                    <p className="text-[11px] text-muted-foreground">{l}</p>
                    <p className="text-lg font-bold tabular-nums">{v}</p>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Portföyü — komite bazlı sorumluluk (mevcut durum)</p>
                <div className="overflow-x-auto rounded-md border border-border max-h-56 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="ur-portfolio-table">
                    <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                      <tr className="text-left text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Komite</th>
                        <th className="px-3 py-2 font-medium text-right">Firma</th>
                        <th className="px-3 py-2 font-medium text-right">Kesin</th>
                        <th className="px-3 py-2 font-medium text-right">Karşı</th>
                        <th className="px-3 py-2 font-medium text-right">Belge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.portfolio_committees || []).map((c, i) => (
                        <tr key={i} className="border-t border-border/50">
                          <td className="px-3 py-1.5">{c.committee_no != null ? `#${c.committee_no} ` : ""}{c.name}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{c.firms}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{c.kesin}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: RED }}>{c.karsi}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{c.belge}</td>
                        </tr>
                      ))}
                      {!(detail.portfolio_committees || []).length && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Sorumlu olduğu firma yok.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Komite bazlı çalışma</p>
                <div className="overflow-x-auto rounded-md border border-border max-h-64 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="ur-committee-table">
                    <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                      <tr className="text-left text-muted-foreground">
                        <cmT.Th sortKey="uc-komite" get={(r) => r.name} text className="px-3 py-2 font-medium">Komite</cmT.Th>
                        <cmT.Th sortKey="uc-dk" get={(r) => r.minutes} className="px-3 py-2 font-medium text-right">Dakika</cmT.Th>
                        <cmT.Th sortKey="uc-degisiklik" get={(r) => r.changes} className="px-3 py-2 font-medium text-right">Değişiklik</cmT.Th>
                        <cmT.Th sortKey="uc-kazanc" get={(r) => r.gained} className="px-3 py-2 font-medium text-right">Kesin +</cmT.Th>
                        <cmT.Th sortKey="uc-kayip" get={(r) => r.lost} lowBest className="px-3 py-2 font-medium text-right">Karşı +</cmT.Th>
                        <cmT.Th sortKey="uc-net" get={(r) => r.net} className="px-3 py-2 font-medium text-right">Net</cmT.Th>
                      </tr>
                    </thead>
                    <tbody>
                      {cmT.applySort(detail.committees).map((c, i) => (
                        <tr key={i} className="border-t border-border/50">
                          <td className="px-3 py-1.5">{c.committee_no != null ? `#${c.committee_no} ` : ""}{c.name}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{c.minutes}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{c.changes}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: GREEN }}>{c.gained}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: RED }}>{c.lost}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{c.net}</td>
                        </tr>
                      ))}
                      {!detail.committees.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Kayıt yok.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium mb-2">Oturumlar</p>
                  <div className="overflow-x-auto rounded-md border border-border max-h-56 overflow-y-auto">
                    <table className="w-full text-xs" data-testid="ur-sessions-table">
                      <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                        <tr className="text-left text-muted-foreground">
                          <seT.Th sortKey="us-giris" get={(r) => r.login_at} text className="px-3 py-2 font-medium">Giriş</seT.Th>
                          <seT.Th sortKey="us-cikis" get={(r) => r.logout_at || r.last_seen_at} text className="px-3 py-2 font-medium">Çıkış / son</seT.Th>
                          <seT.Th sortKey="us-sure" get={(r) => r.minutes} className="px-3 py-2 font-medium text-right">Süre</seT.Th>
                          <seT.Th sortKey="us-ip" get={(r) => r.ip} text className="px-3 py-2 font-medium">IP</seT.Th>
                        </tr>
                      </thead>
                      <tbody>
                        {seT.applySort(detail.sessions).map((s, i) => (
                          <tr key={i} className="border-t border-border/50">
                            <td className="px-3 py-1.5">{fmtDt(s.login_at)}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{fmtDt(s.logout_at || s.last_seen_at)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmtMin(s.minutes)}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{s.ip || "—"}</td>
                          </tr>
                        ))}
                        {!detail.sessions.length && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Oturum kaydı yok.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">Günlük çalışma</p>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={detail.daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Line type="monotone" dataKey="minutes" name="Dakika" stroke={NAVY} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="changes" name="Değişiklik" stroke={AMBER} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="gained" name="Kazanılan" stroke={GREEN} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Sorumlusu olduğu firmalardaki işlemler ({detail.changes.length})</p>
                <div className="overflow-x-auto rounded-md border border-border max-h-72 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="ur-changes-table">
                    <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                      <tr className="text-left text-muted-foreground">
                        <chT.Th sortKey="ud-zaman" get={(r) => r.timestamp} text className="px-3 py-2 font-medium">Zaman</chT.Th>
                        <chT.Th sortKey="ud-firma" get={(r) => r.entity_name} text className="px-3 py-2 font-medium">Firma / Kayıt</chT.Th>
                        <chT.Th sortKey="ud-komite" get={(r) => r.committee_no} className="px-3 py-2 font-medium">Komite</chT.Th>
                        <chT.Th sortKey="ud-giren" get={(r) => r.actor} text className="px-3 py-2 font-medium">İşlemi giren</chT.Th>
                        <chT.Th sortKey="ud-islem" get={(r) => r.action} text className="px-3 py-2 font-medium">İşlem</chT.Th>
                        <th className="px-3 py-2 font-medium">Değişiklik</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chT.applySort(detail.changes).map((c, i) => (
                        <tr key={i} className="border-t border-border/50">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDt(c.timestamp)}</td>
                          <td className="px-3 py-1.5">{c.entity_name || "—"}</td>
                          <td className="px-3 py-1.5">{c.committee_no != null ? `#${c.committee_no}` : "—"}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{c.actor || "—"}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{c.action}</td>
                          <td className="px-3 py-1.5">
                            {c.fields.length ? c.fields.map((f, j) => (
                              <span key={j} className="inline-block mr-2">
                                <span className="text-muted-foreground">{f.field}:</span>{" "}
                                <span className="line-through opacity-60">{String(f.old ?? "boş")}</span>{" → "}
                                <span className="font-medium">{String(f.new ?? "boş")}</span>
                              </span>
                            )) : "—"}
                          </td>
                        </tr>
                      ))}
                      {!detail.changes.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Değişiklik kaydı yok.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
